import React, { useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { Card, Tabs, Space, Button, Spin } from 'antd'
import { PageHeader } from '@ant-design/pro-components'
import { LoadingOutlined } from '@ant-design/icons'

import { Container } from './styles'
import Skills from '~/components/DataManipulation/Skill/Skills'
import SkillsList from '~/components/DataManipulation/Skill/SkillsList'
import AddressModal from '~/components/DataManipulation/Address/AddressModal'
import AddressList from '~/components/DataManipulation/Address/AddressList'
import AttachmentsModal from '~/components/DataManipulation/Attachment/AttachmentsModal'
import AttachmentsList from '~/components/DataManipulation/Attachment/AttachmentsList'
import ContributorDetails from '~/components/DataManipulation/Contributor/ContributorForm/ContributorDetails'
import EvaluationModal from '~/components/DataManipulation/Evaluation/EvaluationModal'
import EvaluationList from '~/components/DataManipulation/Evaluation/EvaluationList'

import { useContributor } from '~/hooks/Contributors/useContributor'

import { useListTask } from '~/hooks/Tasks/useListTasks'
import { useListClients } from '~/hooks/Clients/useListClients'

import { translate } from '~/utils/locale'
import HelpTooltip from '../../Misc/HelpTooltip'
import ContributorFinancial from '../ContributorFinancial'
import ContributorUpdate from '../ContributorForm/ContributorUpdate'

interface ContributorTabsProps {
  isCreation?: boolean
}

const ContributorTabs: React.FC<ContributorTabsProps> = () => {
  const history = useHistory()

  const { contributorId, contactId, tab } = useParams<{
    contributorId: string
    contactId: string
    tab: string
  }>()

  const [activeKey, setActiveKey] = useState(tab ? `${tab}` : 'detalhes')

  const {
    isLoading,
    isFetching,
    contributorAddress,

    contributorRefetch,
    isEdit,
  } = useContributor()

  const { handleUpdateFilters } = useListTask()
  const { handleUpdateFilters: filtersClient } = useListClients()

  const { TabPane } = Tabs

  function handleFilterTask() {
    const dataTask = {
      responsibleContributors: contributorId,
    }

    handleUpdateFilters(dataTask)
    history.push('/agenda')
  }

  function handleFilterClient() {
    const dataClient = {
      collaborator_id: [contributorId],
    }

    filtersClient(dataClient)
    history.push('/clientes')
    window.location.reload()
  }

  const handleTabChange = (key: string) => {
    const path = window.location.pathname.split('/')

    if (key === 'detalhes') {
      history.replace({
        pathname: `/colaboradores/${path[2]}/colaborador/${contributorId}/contato/${contactId}`,
      })
    } else {
      history.replace({
        pathname: `/colaboradores/${path[2]}/colaborador/${contributorId}/contato/${contactId}/${key}`,
      })
    }
  }

  return (
    <Container>
      <PageHeader
        className="site-page-header"
        onBack={() => window.history.back()}
        title={
          isEdit
            ? translate('ContributorTabs.pageheaderEditTitle')
            : translate('ContributorTabs.pageheaderViewTitle')
        }
      />
      <Tabs
        tabBarExtraContent={
          <Space size="large">
            {!isLoading && isFetching && <LoadingOutlined spin />}
            <Space>
              <Button
                id="btn-view-tasks"
                type="primary"
                onClick={handleFilterTask}
              >
                {translate('ContributorTabs.seeTaskButton')}
              </Button>
              <Button
                id="btn-view-clients"
                type="primary"
                onClick={handleFilterClient}
              >
                {translate('ContributorTabs.seeClientsButton')}
              </Button>
            </Space>
          </Space>
        }
        onTabClick={(key: string) => {
          handleTabChange(key)
          setActiveKey(key)
        }}
        activeKey={activeKey}
        defaultActiveKey={tab ? `${tab}` : 'detalhes'}
        style={{ paddingLeft: 20, paddingRight: 20 }}
      >
        <TabPane tab={translate('ContributorTabs.detailsTab')} key="detalhes">
          {isEdit ? (
            <ContributorUpdate />
          ) : isLoading || isFetching ? (
            <Spin size="large" indicator={<LoadingOutlined spin />} />
          ) : (
            <ContributorDetails />
          )}
        </TabPane>

        <TabPane tab={translate('ContributorTabs.addressTab')} key="enderecos">
          <Card
            bordered={false}
            title={translate('ContributorTabs.addressCardTitle')}
            extra={<AddressModal refetch={contributorRefetch} />}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <AddressList
                refetch={contributorRefetch}
                addressInfo={contributorAddress}
              />
            </Space>
          </Card>
        </TabPane>

        <TabPane tab={translate('ContributorTabs.documentsTab')} key="anexos">
          <Card
            bordered={false}
            extra={<AttachmentsModal />}
            title={translate('ContributorTabs.documentsCardTitle')}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <AttachmentsList />
            </Space>
          </Card>
        </TabPane>

        <TabPane tab={translate('ContributorTabs.skillsTab')} key="habilidades">
          <Card
            bordered={false}
            title={translate('ContributorTabs.skillsCardTitle')}
            extra={<Skills />}
            style={{
              display: 'inline-block',
              width: '100%',
            }}
          >
            <SkillsList />
          </Card>
        </TabPane>

        <TabPane
          tab={translate('ContributorTabs.evaluationTab')}
          key="avaliacoes"
        >
          <Card
            bordered={false}
            extra={<EvaluationModal />}
            title={
              <Space>
                {translate('ContributorTabs.evaluationCardTitle')}
                <HelpTooltip
                  title={translate('ContributorTabs.evaluationCardHelp')}
                />
              </Space>
            }
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <EvaluationList />
            </Space>
          </Card>
        </TabPane>

        <TabPane tab="Valores Acordados" key="financial">
          <Card
            bordered={false}
            title={<Space size="large">Valores Acordados</Space>}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <ContributorFinancial />
            </Space>
          </Card>
        </TabPane>
      </Tabs>
    </Container>
  )
}

export default ContributorTabs
